// Maps a saved Client-Calculator quote (vexos_quotes record) into the shape the
// Intake form needs: client prefill, a human scope descriptor, a delivery
// estimate derived from the quote's parallel production timeline, and a
// read-only payment schedule. Kept framework-free so both the intake gate and
// the Quote Link Manager can reuse it.

import { resolveV1Milestone, quoteMilestonePolicy } from "../../pages/calculators/paymentPlan";

export function fmtUSD(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// ── Scope / service type ────────────────────────────────────────────────
// Mirrors the signals used by the backend's detect_proposal_mode(). Returns a
// backend enum value ("Renderings" | "Marketing" | "Both") plus a human string.
export function deriveScope(quote) {
  const inputs = quote?.inputs || {};
  const tab = inputs.activeTab;
  const marketingActive = !!(quote?.marketing && quote.marketing.active);
  const collateralActive = !!(quote?.collateral && quote.collateral.active);

  if (tab === "lotexplorer" || inputs?.lotExplorer?.enabled) {
    return { serviceType: "Renderings", summary: "Lot Explorer" };
  }
  if (inputs.isAlacarte || tab === "alacarte") {
    const summary = marketingActive || collateralActive
      ? "Visualization (À La Carte) + Marketing"
      : "Visualization (À La Carte)";
    return { serviceType: marketingActive || collateralActive ? "Both" : "Renderings", summary };
  }

  // VEXOS is disabled when the rep explicitly turned it off but still has a
  // marketing/collateral scope — that's a marketing-only proposal.
  const vexosOff = inputs.vexosEnabled === false;
  if (vexosOff && (marketingActive || collateralActive)) {
    return { serviceType: "Marketing", summary: "Marketing" };
  }

  // Default: VEXOS platform + Visualization, plus Marketing if layered on.
  if (marketingActive || collateralActive) {
    return { serviceType: "Both", summary: "VEXOS + Visualization + Marketing" };
  }
  return { serviceType: "Renderings", summary: "VEXOS + Visualization" };
}

// ── Timeline ────────────────────────────────────────────────────────────
// VEXOS and Visualization run in PARALLEL, so the total is the max of the two
// tracks, not the sum. Returns display strings for each track plus the total
// weeks and the computed delivery date from a given start date.
export function deriveTimeline(quote, startDate) {
  const t = quote?.timeline || {};
  const vexosDays = Number(t.vexosBusinessDays || 0);
  const vizDays = Number(t.vizBusinessDays || 0);
  const totalBusinessDays = Math.max(vexosDays, vizDays);
  const hasTimeline = totalBusinessDays > 0;
  // Business days → calendar weeks (5 business days per week).
  const totalWeeks = hasTimeline ? Math.max(1, Math.round(totalBusinessDays / 5)) : null;

  let deliveryDate = "";
  if (startDate && hasTimeline) {
    const calendarDays = Math.ceil((totalBusinessDays / 5) * 7);
    const d = new Date(startDate);
    d.setDate(d.getDate() + calendarDays);
    deliveryDate = d.toISOString().split("T")[0];
  }

  return {
    hasTimeline,
    vexosDays,
    vizDays,
    totalBusinessDays,
    totalWeeks,
    vexosWeeksDisplay: t.vexosWeeksDisplay || (vexosDays ? `~${Math.round(vexosDays / 5)} weeks` : null),
    vizWeeksDisplay: t.vizWeeksDisplay || (vizDays ? `~${Math.round(vizDays / 5)} weeks` : null),
    deliveryDate,
  };
}

// ── Contract total ─────────────────────────────────────────────────────
// calc_snapshot.grandTotal is the VISUALIZATION + platform total only. The
// collateral built on the calculator's Marketing tab (website, landing page,
// flipbook…) is saved beside it on `quote.collateral`, and the payment
// schedule bills it inside Payments 1-3 — so a total read off the snapshot
// alone came up short by exactly the collateral (Brandon Sudweeks: rows summed
// to $27,000, total said $23,000, the $4,000 website missing).
export function collateralOneTimeOf(quote) {
  const col = quote?.collateral || {};
  return col.active ? Number(col.collateralTotal || 0) : 0;
}

// The ONE-TIME scope: setup + visualization (+ travel, discounts and fees the
// calculator already folded in). Quotes saved before oneTimeAllIn existed fall
// back to grandTotal minus the term's subscription.
export function quoteOneTimeTotal(quote) {
  const snap = quote?.calc_snapshot || {};
  if (snap.oneTimeAllIn != null) return Number(snap.oneTimeAllIn || 0);
  return Math.max(0, Number(snap.grandTotal || 0) - quoteSubscriptionTotal(quote));
}

// The platform subscription for the whole term — the same monthly × term the
// project's subscription ledger is built from (payment_schedule.monthly is the
// effective figure, after any CRM deduction or rep override).
export function quoteSubscriptionTotal(quote) {
  const ps = quote?.payment_schedule || {};
  return Number(ps.monthly || 0) * Number(ps.term || quote?.contract_term || 0);
}

// Contract value = one-time scope + the subscription term + collateral
// (Bernard, 2026-10-27: "id like the contract value to include the
// subscription"). Paid subscription months count toward cash_collected to
// match, so exposure stays right — see services/payments.py.
export function quoteContractTotal(quote) {
  return quoteOneTimeTotal(quote) + quoteSubscriptionTotal(quote) + collateralOneTimeOf(quote);
}

// ── Payment ─────────────────────────────────────────────────────────────
// Turns the quote's payment_schedule + calc_snapshot into displayable
// milestones. Milestone labels match the calculator's own proposal wording.
export function derivePayment(quote) {
  const ps = quote?.payment_schedule || {};
  const snap = quote?.calc_snapshot || {};
  const isAlacarte = !!ps.isAlacarte;
  const travelCost = Number(ps.travelCost || 0);
  const monthly = Number(ps.monthly || 0);
  const term = Number(ps.term || quote?.contract_term || 0);
  const collateralTotal = collateralOneTimeOf(quote);
  const grandTotal = quoteContractTotal(quote);

  // Percentages are derived from the SAVED amounts, never hardcoded, so the
  // notes describe whichever plan was persisted: 50/25/25, 25/50/25 (quotes
  // over $100k one-time), the à la carte 50/50, or a legacy split written
  // under an older threshold.
  const p1 = Number(ps.p1 || 0);
  const p2 = Number(ps.p2 || 0);
  const p3 = Number(ps.p3 || 0);
  // The setup fee joins the split on 50/25/25 and bills whole at signing on
  // the over-$100k 25/50/25 — read which from the saved schedule.
  const setupInSplit = !!ps.setupInSplit;
  const setupFee = isAlacarte || setupInSplit ? 0 : Number(snap.setupFee || 0);
  const splitTotal = (p1 - travelCost - setupFee) + p2 + p3;
  // Collateral rides the plan percentages whenever there is a split to ride,
  // so the notes have to name it or they call a website "visualization".
  const collateralInSplit = collateralTotal > 0 && splitTotal - collateralTotal > 0.5;
  const scopeLabel = [
    setupInSplit && "VEXOS setup",
    "visualization",
    collateralInSplit && "collateral",
  ].filter(Boolean).join(" + ") + " scope";
  const pct = (amt) => (splitTotal > 0 ? `${Math.round((amt / splitTotal) * 100)}% of ${scopeLabel}` : "Visualization installment");

  const p1Note = `${setupFee > 0 ? "VEXOS setup + " : ""}${pct(p1 - travelCost - setupFee)}${travelCost > 0 ? " + on-site travel" : ""}`;
  const p2Note = pct(p2);
  const p3Note = pct(p3);
  // Payment 2's trigger, under the terms THIS quote states. On a quote written
  // after the rename that is the V1 Clay package — one ReviewStudio delivery
  // holding the clay orbit and keyframes, the clay stills and any clay 360
  // tours (quotes with no clay stage at all, i.e. floor plans, gate on their
  // own V1). On an older quote it is whatever that quote was sold under.
  const v1 = resolveV1Milestone(snap, {
    isAlacarte,
    milestonePolicy: quoteMilestonePolicy(quote),
  });

  const milestones = [
    { label: "Payment 1 — Due at Signing", note: p1Note, amount: p1 },
    // The à la carte 50/50 plan's second payment IS the finalization payment.
    { label: p3 > 0 ? `Payment 2 — Due at ${v1.shortLabel}` : "Payment 2 — Due on Finalization", note: p2Note, amount: p2 },
  ];
  // Two-payment plans have no third milestone — don't list a $0 one.
  if (p3 > 0) {
    // Ops needs to know this one is collected BEFORE the files go out.
    milestones.push({
      label: "Payment 3 — Due on Finalization",
      note: `${p3Note} — collected prior to final delivery`,
      amount: p3,
    });
  }

  // Human-readable terms string persisted onto the intake for the Ops team.
  const parts = milestones.map((m) => `${fmtUSD(m.amount)} ${m.label.replace(/^Payment \d+ — /, "")}`);
  if (monthly > 0 && term > 0) parts.push(`${fmtUSD(monthly)}/mo × ${term} months`);
  const termsSummary = parts.join("; ");

  return {
    isAlacarte,
    milestones,
    monthly,
    term,
    grandTotal,
    oneTime: quoteOneTimeTotal(quote) + collateralTotal,
    depositP1: Number(ps.p1 || 0),
    termsSummary,
  };
}

// ── Accounts Payable ────────────────────────────────────────────────────
// The quote names one person — the client who signs — and on most deals that
// is also who pays, so picking a quote fills the AP contact with them instead
// of leaving the rep to retype the name and email the quote already holds
// (Julian, 2026-10-26). It stays editable for the deals where a bookkeeper
// pays. Only an AP the rep hasn't touched is replaced: a blank one, or one
// still holding the client of the quote they just switched away from.
export function apFromQuote(quote) {
  return {
    name: (quote?.client_name || "").trim(),
    email: (quote?.client_email || "").trim(),
  };
}

export function prefillAccountsPayable(currentAP, prevQuote, nextQuote) {
  const ap = currentAP || { name: "", email: "", title: "", phone: "" };
  const name = (ap.name || "").trim();
  const email = (ap.email || "").trim();
  const prev = prevQuote ? apFromQuote(prevQuote) : null;
  const untouched = (!name && !email) || (prev && name === prev.name && email === prev.email);
  if (!untouched) return ap;
  return { ...ap, ...apFromQuote(nextQuote) };
}

// ── Signed agreement ─────────────────────────────────────────────────────
export function quoteHasDocusign(quote) {
  return Boolean(quote?.docusign_envelope_id);
}

export function quoteDocusignSigned(quote) {
  return (quote?.docusign_status || "").toLowerCase() === "completed";
}

// ── Full prefill for the intake form ──────────────────────────────────────
export function buildQuotePrefill(quote) {
  const scope = deriveScope(quote);
  const pay = derivePayment(quote);
  return {
    source_quote_id: quote?.id || null,
    client_name: quote?.client_name || "",
    client_entity: quote?.client_entity || "",
    client_email: quote?.client_email || "",
    service_type: scope.serviceType,
    service_summary: scope.summary,
    // A quote sent through DocuSign carries its own signed PDF — HQ pulls it
    // from the envelope, so the rep has nothing to paste or upload.
    ...(quoteHasDocusign(quote) ? { agreement_type: "docusign", signed_agreement_link: "", agreement_file: null } : {}),
    contract_value: pay.grandTotal ? String(pay.grandTotal) : "",
    expected_deposit: pay.depositP1 ? String(pay.depositP1) : "",
    payment_terms: pay.termsSummary,
  };
}
